import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listCustomers,
  createCustomer,
  listMembers,
  type CustomerKind,
  type CustomerStatus,
} from "@/lib/api";
import { CUSTOMER_STATUS_META, fmtMoney } from "@/lib/crm";
import { Modal } from "@/components/Modal";
import { Avatar } from "@/components/Avatar";
import { EntityLogo } from "@/components/EntityLogo";
import { CustomerModal } from "@/components/crm/CustomerModal";

export function CustomersPage() {
  const { data: customers, isLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: listCustomers,
  });
  const [addOpen, setAddOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div>
      <header className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="page-title">Customers</h1>
          <p className="mt-2 text-text-secondary">
            Company records — contacts, notes, and their deals on the{" "}
            <a href="/pipeline" className="text-accent hover:underline">
              Pipeline
            </a>
            .
          </p>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
          style={{ color: "var(--color-on-accent)" }}
        >
          <i className="fa-solid fa-plus mr-1.5 text-xs" />
          Add customer
        </button>
      </header>

      <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
        {isLoading ? (
          <div className="grid place-items-center py-16 text-text-muted">
            <i className="fa-solid fa-circle-notch fa-spin text-lg" />
          </div>
        ) : !customers || customers.length === 0 ? (
          <EmptyState onAdd={() => setAddOpen(true)} />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[0.65rem] uppercase tracking-wider text-text-muted">
                <th className="px-4 py-2.5 font-semibold">Name</th>
                <th className="w-24 px-4 py-2.5 font-semibold">Status</th>
                <th className="w-36 px-4 py-2.5 font-semibold">Owner</th>
                <th className="w-28 px-4 py-2.5 font-semibold">Last contact</th>
                <th className="w-24 px-4 py-2.5 text-center font-semibold">Contacts</th>
                <th className="w-32 px-4 py-2.5 text-right font-semibold">Open deals</th>
                <th className="w-28 px-4 py-2.5 text-right font-semibold">Won</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => {
                const meta = CUSTOMER_STATUS_META[c.status];
                return (
                  <tr
                    key={c.id}
                    onClick={() => setOpenId(c.id)}
                    className="cursor-pointer border-b border-border-light transition-colors hover:bg-bg-card-hover"
                  >
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-2.5">
                        <EntityLogo
                          name={c.name}
                          url={c.logoUrl}
                          size={26}
                          fallbackIcon={c.kind === "individual" ? "fa-user" : "fa-building"}
                        />
                        <span className="font-medium text-text-heading">{c.name}</span>
                      </span>
                      {c.website && (
                        <a
                          href={c.website}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="ml-2 text-xs text-text-muted hover:text-accent"
                          title={c.website}
                        >
                          <i className="fa-solid fa-arrow-up-right-from-square" />
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs text-text-secondary">
                        <span
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ background: meta.color }}
                        />
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {c.owner ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-text-secondary">
                          <Avatar name={c.owner.name} url={c.owner.avatarUrl} size={20} />
                          <span className="truncate">{c.owner.name}</span>
                        </span>
                      ) : (
                        <span className="text-xs text-text-muted">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-text-secondary">
                      {c.lastSessionAt ? (
                        new Date(c.lastSessionAt).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                        })
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-text-secondary">
                      {c.contactCount || "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-text-secondary">
                      {c.openDeals ? `${c.openDeals} · ${fmtMoney(c.openValue)}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-text-heading">
                      {c.wonValue ? fmtMoney(c.wonValue) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {addOpen && <NewCustomerModal onClose={() => setAddOpen(false)} onCreated={setOpenId} />}
      {openId && <CustomerModal customerId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="grid place-items-center py-16 text-center">
      <div className="max-w-sm">
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-token-lg bg-accent-muted text-2xl text-accent">
          <i className="fa-solid fa-building-user" />
        </div>
        <h2 className="font-display text-lg font-semibold text-text-heading">
          No customers yet
        </h2>
        <p className="mx-auto mt-1.5 text-sm text-text-secondary">
          Add your first customer — contacts, notes, and deals all hang off the company
          record.
        </p>
        <button
          onClick={onAdd}
          className="mt-4 rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
          style={{ color: "var(--color-on-accent)" }}
        >
          <i className="fa-solid fa-plus mr-1.5 text-xs" />
          Add customer
        </button>
      </div>
    </div>
  );
}

function NewCustomerModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const { data: members } = useQuery({ queryKey: ["members"], queryFn: listMembers });
  const [name, setName] = useState("");
  const [kind, setKind] = useState<CustomerKind>("company");
  const [status, setStatus] = useState<CustomerStatus>("lead");
  const [website, setWebsite] = useState("");
  const [industry, setIndustry] = useState("");
  const [ownerId, setOwnerId] = useState("");

  const create = useMutation({
    mutationFn: () =>
      createCustomer({
        name,
        kind,
        status,
        website: website || undefined,
        industry: industry || undefined,
        ownerId: ownerId || undefined,
      }),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      onClose();
      onCreated(c.id);
    },
  });

  const inputCls =
    "mb-3 w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";

  return (
    <Modal open onClose={onClose} title="Add customer">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <label className="mb-1 block text-xs font-medium text-text-muted">Type</label>
        <div className="mb-3 flex overflow-hidden rounded-token-md border border-border">
          {(["company", "individual"] as CustomerKind[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                kind === k
                  ? "bg-accent-muted text-accent"
                  : "text-text-secondary hover:text-text-heading"
              }`}
            >
              <i className={`fa-solid ${k === "company" ? "fa-building" : "fa-user"} mr-1.5 text-xs`} />
              {k === "company" ? "Company" : "Individual"}
            </button>
          ))}
        </div>

        <label className="mb-1 block text-xs font-medium text-text-muted">
          {kind === "company" ? "Company name" : "Full name"}
        </label>
        <input
          className={inputCls}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          required
        />

        <div className="flex gap-3">
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-text-muted">Status</label>
            <select
              className={inputCls}
              value={status}
              onChange={(e) => setStatus(e.target.value as CustomerStatus)}
            >
              {(Object.keys(CUSTOMER_STATUS_META) as CustomerStatus[]).map((s) => (
                <option key={s} value={s}>
                  {CUSTOMER_STATUS_META[s].label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-text-muted">Industry</label>
            <input
              className={inputCls}
              value={industry}
              placeholder="e.g. SaaS"
              onChange={(e) => setIndustry(e.target.value)}
            />
          </div>
        </div>

        <label className="mb-1 block text-xs font-medium text-text-muted">Website</label>
        <input
          className={inputCls}
          value={website}
          placeholder="https://…"
          onChange={(e) => setWebsite(e.target.value)}
        />

        <label className="mb-1 block text-xs font-medium text-text-muted">Owner</label>
        <select className={inputCls} value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
          <option value="">Unassigned</option>
          {members?.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>

        <div className="mt-1 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-token-md border border-border px-4 py-2 text-sm text-text-secondary hover:text-text-heading"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ color: "var(--color-on-accent)" }}
          >
            {create.isPending ? "Adding…" : "Add customer"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
